import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApi, postApi } from '../../Services/apiService';
import { toast } from 'react-toastify';

const DEFAULT_RADIUS = 25;

const MyProperties = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);

  // Location filter state
  const [locationDisplay, setLocationDisplay] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const [locationLat, setLocationLat] = useState(null);
  const [locationLng, setLocationLng] = useState(null);
  const [locationRadius] = useState(DEFAULT_RADIUS);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [loadingLocationSuggestions, setLoadingLocationSuggestions] = useState(false);
  const [gettingUserLocation, setGettingUserLocation] = useState(false);
  const [locationInputFocused, setLocationInputFocused] = useState(false);
  const isSelectingLocationRef = useRef(false);
  const locationSuggestionsRef = useRef(null);
  const locationDebounceRef = useRef(null);
  const locationBlurTimeoutRef = useRef(null);

  // API state
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    totalItems: 0
  });

  const searchTimeoutRef = useRef(null);
  const loadMoreRef = useRef(null);

  // Transform API response to component format
  const transformProperty = (apiProperty) => {
    const firstImage = apiProperty.property_images && apiProperty.property_images.length > 0
      ? apiProperty.property_images[0].image_url
      : 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=400';

    const address = apiProperty.address || '';
    const unitNumber = apiProperty.unit_number ? `, ${apiProperty.unit_number}` : '';
    const fullAddress = `${address}${unitNumber}`;

    const cityState = apiProperty.city && apiProperty.state
      ? `${apiProperty.city}, ${apiProperty.state}`
      : apiProperty.city || apiProperty.state || '';

    const price = apiProperty.monthly_rent
      ? `$${Number(apiProperty.monthly_rent).toLocaleString()}/mo`
      : 'N/A';

    const bedrooms = apiProperty.bedrooms
      ? `${apiProperty.bedrooms}${apiProperty.bedrooms === 1 ? 'BHK' : 'BHK'}`
      : '';

    const sqft = apiProperty.square_feet
      ? `${Number(apiProperty.square_feet).toLocaleString()} sq.ft`
      : '';

    const listedDate = apiProperty.listed_at
      ? new Date(Number(apiProperty.listed_at)).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })
      : '';

    const code = apiProperty.property_code || (apiProperty.id ? String(apiProperty.id).substring(0, 5) : '');

    return {
      id: apiProperty.id,
      image: firstImage,
      address: fullAddress,
      code: code,
      city: cityState,
      price: price,
      bedrooms: bedrooms,
      sqft: sqft,
      status: (String(apiProperty.status) === '1' || apiProperty.status === 'active' || apiProperty.status === 'Active') ? 'Active' : 'Inactive',
      applicants: Number(apiProperty.applicants_count) || 0,
      listedDate: listedDate,
      llcName: apiProperty.llc_name || '',
      llcLogo: apiProperty.llc_logo || ''
    };
  };

  // Fetch properties from GET /tenant/properties
  const fetchProperties = async (page = 1, append = false, search = '', lat = null, lng = null, radius = null) => {
    const limit = pagination.pageSize;
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit)
      });
      if (search && search.trim()) params.append('search', search.trim());
      if (lat != null && lat !== '') params.append('lat', String(lat));
      if (lng != null && lng !== '') params.append('lng', String(lng));
      if (radius != null && radius !== '') params.append('radius', String(radius));

      const response = await getApi(`/tenant/properties?${params.toString()}`);

      if (response?.success !== false && response?.data) {
        const data = response.data;
        const list = Array.isArray(data.data) ? data.data : [];
        const transformedProperties = list.map(transformProperty);
        setProperties((prev) => (append ? [...prev, ...transformedProperties] : transformedProperties));
        setPagination({
          currentPage: data.currentPage ?? page,
          pageSize: data.pageSize ?? limit,
          totalPages: data.totalPages ?? 1,
          totalItems: data.totalItems ?? 0
        });
      } else if (!append) {
        setProperties([]);
      }
    } catch (error) {
      console.error('Error fetching tenant properties:', error);
      if (!append) setProperties([]);
    } finally {
      if (append) {
        setIsLoadingMore(false);
      } else {
        setIsLoading(false);
      }
    }
  };

  // Debounce search and reset to page 1 when search changes
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [searchQuery]);

  // Debounced location autocomplete
  useEffect(() => {
    if (isSelectingLocationRef.current) return;
    if (!locationInput || locationInput.trim().length < 3) {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }
    if (locationDebounceRef.current) clearTimeout(locationDebounceRef.current);
    locationDebounceRef.current = setTimeout(async () => {
      setLoadingLocationSuggestions(true);
      try {
        const response = await getApi(`/property/autocomplete?input=${encodeURIComponent(locationInput.trim())}`);
        if (response?.statusCode === 200 && response?.data?.suggestions) {
          setLocationSuggestions(response.data.suggestions);
          setShowLocationSuggestions(true);
        } else {
          setLocationSuggestions([]);
          setShowLocationSuggestions(false);
        }
      } catch (error) {
        console.error('Error fetching location suggestions:', error);
        setLocationSuggestions([]);
        setShowLocationSuggestions(false);
      } finally {
        setLoadingLocationSuggestions(false);
      }
    }, 400);
    return () => { if (locationDebounceRef.current) clearTimeout(locationDebounceRef.current); };
  }, [locationInput]);

  const handleLocationSelect = async (suggestion) => {
    isSelectingLocationRef.current = true;
    setShowLocationSuggestions(false);
    setLocationSuggestions([]);
    setLocationDisplay(suggestion.description);
    setLocationInput(suggestion.description);
    try {
      const response = await postApi('/property/place-details', { place_id: suggestion.place_id });
      if (response?.statusCode === 200 && response?.data) {
        const placeData = response.data?.data || response.data;
        const lat = placeData.latitude ?? placeData.lat;
        const lng = placeData.longitude ?? placeData.lng;
        if (lat != null && lng != null) {
          setLocationLat(Number(lat));
          setLocationLng(Number(lng));
        } else {
          setLocationLat(null);
          setLocationLng(null);
          toast.info('Coordinates not available for this place. Try another or use "Use my location".');
        }
      } else {
        setLocationLat(null);
        setLocationLng(null);
      }
    } catch (error) {
      console.error('Error fetching place details:', error);
      setLocationLat(null);
      setLocationLng(null);
      toast.error('Could not get location coordinates.');
    } finally {
      isSelectingLocationRef.current = false;
    }
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser.');
      return;
    }
    setGettingUserLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocationLat(position.coords.latitude);
        setLocationLng(position.coords.longitude);
        setLocationDisplay('Current location');
        setLocationInput('Current location');
        setShowLocationSuggestions(false);
        setLocationSuggestions([]);
        setGettingUserLocation(false);
      },
      (err) => {
        setGettingUserLocation(false);
        toast.error(err.message || 'Could not get your location.');
      }
    );
  };

  const clearLocationFilter = () => {
    setLocationDisplay('');
    setLocationInput('');
    setLocationLat(null);
    setLocationLng(null);
    setShowLocationSuggestions(false);
    setLocationSuggestions([]);
  };

  const hasMore = pagination.currentPage < pagination.totalPages;

  const loadMore = useCallback(() => {
    if (isLoading || isLoadingMore || !hasMore) return;
    fetchProperties(
      pagination.currentPage + 1,
      true,
      debouncedSearchQuery,
      locationLat,
      locationLng,
      locationRadius
    );
  }, [
    isLoading,
    isLoadingMore,
    hasMore,
    pagination.currentPage,
    debouncedSearchQuery,
    locationLat,
    locationLng,
    locationRadius
  ]);

  // Fetch first page when search or location filters change
  useEffect(() => {
    fetchProperties(1, false, debouncedSearchQuery, locationLat, locationLng, locationRadius);
  }, [debouncedSearchQuery, locationLat, locationLng, locationRadius]);

  // Infinite scroll: load next page when sentinel enters viewport
  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { rootMargin: '200px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore, properties.length]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuId !== null) {
        const menuElement = document.querySelector(`.property-card-menu-container[data-property-id="${openMenuId}"]`);
        if (menuElement && !menuElement.contains(event.target)) {
          setOpenMenuId(null);
        }
      }
      if (showLocationSuggestions && locationSuggestionsRef.current && !locationSuggestionsRef.current.contains(event.target)) {
        const input = document.getElementById('tenant-location-filter-input');
        if (input && !input.contains(event.target)) setShowLocationSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId, showLocationSuggestions]);

  useEffect(() => {
    return () => {
      if (locationBlurTimeoutRef.current) clearTimeout(locationBlurTimeoutRef.current);
    };
  }, []);

  const handleMenuToggle = (propertyId, event) => {
    event.preventDefault();
    event.stopPropagation();
    setOpenMenuId(openMenuId === propertyId ? null : propertyId);
  };

  const handleMenuAction = async (action, propertyId, event) => {
    event.stopPropagation();
    setOpenMenuId(null);

    if (action === 'view') {
      navigate(`/tenant/properties/${propertyId}`);
    } else if (action === 'copy') {
      try {
        const response = await postApi(`/property/${propertyId}/share`, {});
        const url = response?.data?.url;
        if (!url) {
          toast.error('Could not get share link');
          return;
        }
        // Prefer modern clipboard API when available (HTTPS/localhost)
        if (navigator?.clipboard && typeof navigator.clipboard.writeText === 'function') {
          await navigator.clipboard.writeText(url);
          toast.success('Link copied to clipboard!');
          return;
        }
        // Fallback for HTTP or when clipboard API is not available
        const textarea = document.createElement('textarea');
        textarea.value = url;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          const successful = document.execCommand('copy');
          if (successful) {
            toast.success('Link copied to clipboard!');
          } else {
            toast.error('Unable to copy link. Please copy it manually.');
          }
        } finally {
          document.body.removeChild(textarea);
        }
      } catch (error) {
        console.error('Error generating share link:', error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to generate share link. Please try again.';
        toast.error(errorMessage);
      }
    }
  };

  const PropertyCardSkeleton = () => (
    <div className="property-card property-card-skeleton" aria-hidden="true">
      <div className="property-card-image-container">
        <div className="property-card-image-shimmer" />
      </div>
      <div className="property-card-content">
        <div className="property-card-skeleton-line property-card-skeleton-line-wide" />
        <div className="property-card-skeleton-line property-card-skeleton-line-medium" />
        <div className="property-card-skeleton-line property-card-skeleton-line-short" />
      </div>
    </div>
  );

  const PropertyCard = ({ property }) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [llcLogoError, setLlcLogoError] = useState(false);
    const imageRef = useRef(null);
    const isMenuOpen = openMenuId === property.id;

    useEffect(() => {
      setImageLoaded(false);
      setImageError(false);
      setLlcLogoError(false);
      const img = imageRef.current;
      if (img?.complete && img.naturalWidth > 0) {
        setImageLoaded(true);
      }
    }, [property.image]);

    return (
      <div className="property-card">
        <div className="property-card-image-container">
          {imageError ? (
            <div className="property-card-image-placeholder" />
          ) : (
            <>
              {!imageLoaded && <div className="property-card-image-shimmer" aria-hidden="true" />}
              <img
                ref={imageRef}
                src={property.image}
                alt={property.address}
                className={`property-card-image${imageLoaded ? ' property-card-image-loaded' : ' property-card-image-loading'}`}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
              />
            </>
          )}
          {(property.llcLogo || property.llcName) && (
            <div className="property-card-llc" title={property.llcName || undefined}>
              {property.llcLogo && !llcLogoError ? (
                <img
                  src={property.llcLogo}
                  alt={property.llcName ? `${property.llcName} logo` : 'Business logo'}
                  className="property-card-llc-logo"
                  onError={() => setLlcLogoError(true)}
                />
              ) : (
                <span className="property-card-llc-fallback" aria-hidden="true">
                  {(property.llcName || '?').trim().charAt(0).toUpperCase()}
                </span>
              )}
              {property.llcName && (
                <span className="property-card-llc-name">{property.llcName}</span>
              )}
            </div>
          )}
          <div 
            className="property-card-menu-container" 
            data-property-id={property.id}
          >
            <button 
              className="property-card-more-button"
              onClick={(e) => handleMenuToggle(property.id, e)}
            >
              <img 
                src="/assets/images/verticalThreeDots.png" 
                alt="More options" 
                className="property-card-more-icon"
              />
            </button>
            {isMenuOpen && (
              <div className="property-card-menu">
                <button 
                  className="property-card-menu-item"
                  onClick={(e) => handleMenuAction('view', property.id, e)}
                >
                  <img 
                    src="/assets/images/Eye.svg" 
                    alt="View" 
                    className="property-card-menu-icon"
                  />
                  <span>View</span>
                </button>
                <button 
                  className="property-card-menu-item"
                  onClick={(e) => handleMenuAction('copy', property.id, e)}
                >
                  <img 
                    src="/assets/images/CopyIcon.svg" 
                    alt="Copy link" 
                    className="property-card-menu-icon"
                  />
                  <span>Copy link</span>
                </button>
              </div>
            )}
          </div>
        </div>
        <div
          className="property-card-content"
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/tenant/properties/${property.id}`)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(`/tenant/properties/${property.id}`);
            }
          }}
        >
          <div className="property-card-top">
            <div className="property-card-header">
              <div className="property-card-address-section">
                <div className="property-card-address" title={property.address}>{property.address}</div>
                <div className="property-card-code">Code: {property.code}</div>
              </div>
              <div className={`property-card-status property-card-status-${property.status.toLowerCase()}`}>
                {property.status}
              </div>
            </div>
            <div className="property-card-location">{property.city}</div>
          </div>
          <div className='property-card-location-flex'>
          <div className="property-card-price">
            {property.price.endsWith('/mo') ? (
              <>
                {property.price.replace(/\/mo$/, '')}
                <span className="property-card-price-suffix">/mo</span>
              </>
            ) : (
              property.price
            )}
          </div>
          <div className="property-card-details">
            {property.bedrooms} {property.sqft}
          </div>
          </div>
          <div className="property-card-footer">
            <div className="property-card-metric">
              <img 
                src="/assets/images/users-profiles-01.png" 
                alt="Applicants" 
                className="property-card-metric-icon"
              />
              <span>{property.applicants} Applicant{property.applicants !== 1 ? 's' : ''}</span>
            </div>
            <div className="property-card-metric">
              <img 
                src="/assets/images/calendar-01.png" 
                alt="Calendar" 
                className="property-card-metric-icon"
              />
              <span>Listed {property.listedDate}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="properties-page">
      <div className="properties-actions-bar">
        <div className="properties-search-wrapper">
          <img 
            src="/assets/images/SearchIcon.svg" 
            alt="Search" 
            className="properties-search-icon"
          />
          <input
            type="text"
            className="properties-search-input"
            placeholder="Search Properties"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="properties-location-filter">
          <label className="properties-location-label">Select Your Location</label>
          <div className="properties-location-input-wrapper" ref={locationSuggestionsRef}>
            <input
              id="tenant-location-filter-input"
              type="text"
              className="properties-location-input"
              placeholder="Select"
              value={locationInput}
              onChange={(e) => setLocationInput(e.target.value)}
              onFocus={() => {
                setLocationInputFocused(true);
                setShowLocationSuggestions(true);
                if (locationBlurTimeoutRef.current) {
                  clearTimeout(locationBlurTimeoutRef.current);
                  locationBlurTimeoutRef.current = null;
                }
              }}
              onBlur={() => {
                locationBlurTimeoutRef.current = setTimeout(() => {
                  setLocationInputFocused(false);
                  setShowLocationSuggestions(false);
                }, 200);
              }}
              autoComplete="off"
            />
            <img src="/assets/images/SearchIcon.svg" alt="" className="properties-location-input-icon" />
            {(locationLat != null || locationDisplay) && (
              <button type="button" className="properties-location-clear" onClick={clearLocationFilter} aria-label="Clear location">
                ×
              </button>
            )}
            {showLocationSuggestions && (
              <div className="properties-location-suggestions">
                <div
                  className={`properties-location-suggestion-item properties-use-my-location-row${gettingUserLocation ? ' properties-use-my-location-row-disabled' : ''}`}
                  onClick={gettingUserLocation ? undefined : handleUseMyLocation}
                  onMouseDown={(e) => e.preventDefault()}
                  role="button"
                  tabIndex={0}
                >
                  <span className="properties-use-my-location-icon" aria-hidden>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  </span>
                  {gettingUserLocation ? 'Getting location...' : 'Use my location'}
                </div>
                {loadingLocationSuggestions ? (
                  <div className="properties-location-suggestion-item properties-location-suggestion-loading">Loading...</div>
                ) : locationSuggestions.length === 0 ? (
                  locationInput.trim().length >= 3 ? (
                    <div className="properties-location-suggestion-item">No suggestions</div>
                  ) : null
                ) : (
                  locationSuggestions.map((suggestion, index) => (
                    <div
                      key={suggestion.place_id || index}
                      className="properties-location-suggestion-item"
                      onClick={() => handleLocationSelect(suggestion)}
                    >
                      <div className="properties-location-suggestion-main">{suggestion.main_text}</div>
                      <div className="properties-location-suggestion-desc">{suggestion.description}</div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', fontSize: '18px', color: '#666' }}>
          Loading properties...
        </div>
      ) : properties.length === 0 ? (
        <div className="properties-empty-state" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '400px', padding: '2rem', textAlign: 'center' }}>
          <p style={{ fontSize: '18px', color: '#666', margin: 0 }}>
            {debouncedSearchQuery || locationLat != null
              ? 'No properties match your search or location. Try adjusting your filters.'
              : 'No properties available at the moment.'}
          </p>
        </div>
      ) : (
        <div className="properties-grid">
          {properties.map((property) => (
            <PropertyCard key={property.id} property={property} />
          ))}
          {isLoadingMore && (
            <>
              <PropertyCardSkeleton />
              <PropertyCardSkeleton />
              <PropertyCardSkeleton />
            </>
          )}
          {hasMore && <div ref={loadMoreRef} className="properties-load-more-sentinel" aria-hidden="true" />}
        </div>
      )}
    </div>
  );
};

export default MyProperties;
